/**
 * PeopleTasks Component
 * 
 * Displays tasks assigned to a specific person.
 * Reuses TasksTable component but with filtered data for the person.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import {
    FilterTasksOffcanvas,
    AddTaskModal,
    MoveOrCopyTaskModal,
    LogTimeModal,
    QuickAddSubtasksModal,
    DeleteTaskModal,
    TasksTable,
    Pagination,
    ViewHistoryModal,
    EstimateTimeModal,
    ContactDetailsModal
} from '../../../components/MyTasks/components';
import {
    priorityOptions,
    privacyOptions,
    assigneeOptions,
    projectOptions,
    taskListOptions,
    stageOptions
} from '../../../components/MyTasks/constants/options';

const PeopleTasks = () => {
    const navigate = useNavigate();
    const { peopleId } = useParams();
    
    const [show, setShow] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showMoveOrCopy, setShowMoveOrCopy] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [moveOrCopyType, setMoveOrCopyType] = useState('move');
    const [targetProject, setTargetProject] = useState(null);
    const [targetTaskList, setTargetTaskList] = useState(null);

    // Log Time Modal state
    const [showLogTime, setShowLogTime] = useState(false);
    const [logDate, setLogDate] = useState(new Date());
    const [logTime, setLogTime] = useState('');
    const [logDescription, setLogDescription] = useState('');

    // Pagination state
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const totalItems = 100; // This would come from API filtered by peopleId
    const totalPages = Math.ceil(totalItems / pageSize);

    // State for filters
    const [taskNameFilter, setTaskNameFilter] = useState('');
    const [projectFilter, setProjectFilter] = useState(null);
    const [taskListFilter, setTaskListFilter] = useState(null);
    const [assigneeFilter, setAssigneeFilter] = useState([]);
    const [dueDateRange, setDueDateRange] = useState([null, null]);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [stageFilter, setStageFilter] = useState(null);

    // State for new task form
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedTaskList, setSelectedTaskList] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [dueDate, setDueDate] = useState(new Date());
    const [estimatedTime, setEstimatedTime] = useState('');
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedPrivacy, setSelectedPrivacy] = useState(null);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [description, setDescription] = useState('');
    const [notifyByEmail, setNotifyByEmail] = useState(false);

    const handleClose = () => {
        setShow(false);
        setSelectedProject(null);
        setSelectedTaskList(null);
        setStartDate(new Date());
        setDueDate(new Date());
        setEstimatedTime('');
        setSelectedPriority(null);
        setSelectedPrivacy(null);
        setSelectedAssignees([]);
        setDescription('');
        setNotifyByEmail(false);
    };
    const handleShow = () => setShow(true);

    const handleDeleteClose = () => setShowDelete(false);
    const handleDeleteShow = () => setShowDelete(true);
    
    const handleDelete = () => {
        handleDeleteClose();
    };

    const handleView = (taskId) => {
        navigate(`/tasks/detail/${taskId || 'TASK-001'}`);
    };

    const handleMoveOrCopyShow = () => {
        setShowMoveOrCopy(true);
    };

    const handleLogTimeShow = () => {
        setShowLogTime(true);
    };

    const handleLogTimeClose = () => {
        setShowLogTime(false);
        setLogDate(new Date());
        setLogTime('');
        setLogDescription('');
    };

    const handleLogTimeSubmit = () => {
        console.log('Log Time:', { logDate, logTime, logDescription });
        handleLogTimeClose();
    };

    const handleContactShow = (contact) => {
        setSelectedContact(contact);
        setShowContact(true);
    };

    const handleContactClose = () => {
        setShowContact(false);
        setSelectedContact(null);
    };

    // NOTE: In real implementation, you would filter tasks by peopleId
    // For now, using same data structure but filtered
    console.log('Loading tasks for person:', peopleId);

    return (
        <>
            <div className='c-card-1'>
                <div className='c-card-header-1'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <div>Tasks</div>
                        <div className='d-flex align-items-center gap-1'>
                            <Button 
                                variant='outline-primary' 
                                className='btn-sm me-2 text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                            <Button 
                                variant='primary' 
                                onClick={handleShow}
                                className='btn-sm text-nowrap'
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add Task
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1 p-0'>
                    <TasksTable
                        onView={handleView}
                        onEdit={handleShow}
                        onDelete={handleDeleteShow}
                        onMoveOrCopy={handleMoveOrCopyShow}
                        onLogTime={handleLogTimeShow}
                        onContactShow={handleContactShow}
                    />
                </div>
                <div className='c-card-footer-1'>
                    <Pagination
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                    />
                </div>
            </div>

            {/* Add Task Modal */}
            <AddTaskModal
                show={show}
                handleClose={handleClose}
                selectedProject={selectedProject}
                setSelectedProject={setSelectedProject}
                selectedTaskList={selectedTaskList}
                setSelectedTaskList={setSelectedTaskList}
                startDate={startDate}
                setStartDate={setStartDate}
                dueDate={dueDate}
                setDueDate={setDueDate}
                estimatedTime={estimatedTime}
                setEstimatedTime={setEstimatedTime}
                selectedPriority={selectedPriority}
                setSelectedPriority={setSelectedPriority}
                selectedPrivacy={selectedPrivacy}
                setSelectedPrivacy={setSelectedPrivacy}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                description={description}
                setDescription={setDescription}
                notifyByEmail={notifyByEmail}
                setNotifyByEmail={setNotifyByEmail}
                priorityOptions={priorityOptions}
                privacyOptions={privacyOptions}
                assigneeOptions={assigneeOptions}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
            />

            {/* Delete Task Modal */}
            <DeleteTaskModal
                show={showDelete}
                handleClose={handleDeleteClose}
                handleDelete={handleDelete}
            />

            {/* Filter Offcanvas */}
            <FilterTasksOffcanvas
                show={showFilter}
                handleClose={() => setShowFilter(false)}
                taskNameFilter={taskNameFilter}
                setTaskNameFilter={setTaskNameFilter}
                projectFilter={projectFilter}
                setProjectFilter={setProjectFilter}
                taskListFilter={taskListFilter}
                setTaskListFilter={setTaskListFilter}
                assigneeFilter={assigneeFilter}
                setAssigneeFilter={setAssigneeFilter}
                dueDateRange={dueDateRange}
                setDueDateRange={setDueDateRange}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                stageFilter={stageFilter}
                setStageFilter={setStageFilter}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
                assigneeOptions={assigneeOptions}
                priorityOptions={priorityOptions}
                stageOptions={stageOptions}
            />

            {/* Move or Copy Modal */}
            <MoveOrCopyTaskModal
                show={showMoveOrCopy}
                handleClose={() => setShowMoveOrCopy(false)}
                type={moveOrCopyType}
                setType={setMoveOrCopyType}
                targetProject={targetProject}
                setTargetProject={setTargetProject}
                targetTaskList={targetTaskList}
                setTargetTaskList={setTargetTaskList}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
            />

            {/* Log Time Modal */}
            <LogTimeModal
                show={showLogTime}
                handleClose={handleLogTimeClose}
                handleSubmit={handleLogTimeSubmit}
                logDate={logDate}
                setLogDate={setLogDate}
                logTime={logTime}
                setLogTime={setLogTime}
                logDescription={logDescription}
                setLogDescription={setLogDescription}
            />

            {/* Contact Details Modal */}
            <ContactDetailsModal
                show={showContact}
                handleClose={handleContactClose}
                contact={selectedContact}
            />
        </>
    );
};

export default PeopleTasks;
